import React, { useState, useEffect } from 'react';
import twoFactorApi from '../../api/twoFactorApi';
import styles from './TwoFactorSetupPage.module.css';

// ─── Icons ────────────────────────────────────────────────────────────────────
function ShieldIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── Steps ────────────────────────────────────────────────────────────────────
// VIEW_STATUS   → estado inicial (cargando / activo / inactivo)
// VIEW_SETUP    → mostrar QR + ingresar dos códigos para activar
// VIEW_DISABLE  → ingresar código para desactivar
// VIEW_SUCCESS  → confirmación de activación/desactivación

export default function TwoFactorSetupPage() {
  const [view, setView] = useState('loading');
  const [enabled, setEnabled] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    twoFactorApi.status()
      .then(data => {
        setEnabled(data.twoFactorEnabled);
        setView('status');
      })
      .catch(() => setView('status'));
  }, []);

  // ── Iniciar setup ──────────────────────────────────────────────────────────
  const handleStartSetup = async () => {
    setErrorMsg('');
    setLoading(true);
    try {
      const data = await twoFactorApi.setup();
      setQrDataUrl(data.qrDataUrl);
      setSecret(data.secret);
      setCode1('');
      setCode2('');
      setView('setup');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Error al generar el QR');
    } finally {
      setLoading(false);
    }
  };

  // ── Activar con dos códigos ────────────────────────────────────────────────
  const handleActivate = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (code1.length !== 6 || code2.length !== 6) {
      setErrorMsg('Cada código debe tener 6 dígitos');
      return;
    }
    setLoading(true);
    try {
      await twoFactorApi.activate(code1, code2);
      setEnabled(true);
      setSuccessMsg('¡2FA activado! Tu cuenta ahora requiere un código de verificación al iniciar sesión.');
      setView('success');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Error al activar el 2FA');
    } finally {
      setLoading(false);
    }
  };

  // ── Desactivar ─────────────────────────────────────────────────────────────
  const handleDisable = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      await twoFactorApi.disable(disableCode);
      setEnabled(false);
      setSuccessMsg('2FA desactivado. Tu cuenta ya no requiere código de verificación.');
      setView('success');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Código incorrecto');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setErrorMsg('');
    setView('status');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (view === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.skeleton} style={{ width: '60%', height: '1.4rem', margin: '0 auto 1rem' }} />
          <div className={styles.skeleton} style={{ width: '80%', height: '1rem', margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  // ── Vista: estado / entrada ────────────────────────────────────────────────
  if (view === 'status') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={`${styles.statusBadge} ${enabled ? styles.statusOn : styles.statusOff}`}>
            <ShieldIcon />
            <span>Autenticación en dos pasos</span>
          </div>

          <p className={styles.statusDesc}>
            {enabled
              ? 'El 2FA está activo en tu cuenta. Cada vez que inicies sesión necesitarás un código de tu aplicación autenticadora.'
              : 'Añade una capa extra de seguridad. Al activarlo, necesitarás un código de Google Authenticator (o similar) para iniciar sesión.'}
          </p>

          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>Estado:</span>
            <span className={`${styles.statusPill} ${enabled ? styles.pillOn : styles.pillOff}`}>
              {enabled ? 'Activo' : 'Inactivo'}
            </span>
          </div>

          {enabled ? (
            <button className={styles.btnDanger} onClick={() => { setErrorMsg(''); setDisableCode(''); setView('disable'); }}>
              Desactivar 2FA
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={handleStartSetup} disabled={loading}>
              {loading ? 'Generando QR…' : 'Activar 2FA'}
            </button>
          )}

          {errorMsg && <p className={styles.error}>{errorMsg}</p>}
        </div>
      </div>
    );
  }

  // ── Vista: setup QR ────────────────────────────────────────────────────────
  if (view === 'setup') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Configura tu autenticador</h2>

          <ol className={styles.steps}>
            <li>Abre <strong>Google Authenticator</strong>, <strong>Authy</strong> u otra app compatible con TOTP.</li>
            <li>Escanea el código QR o ingresa la clave manualmente.</li>
            <li>Ingresa dos códigos consecutivos (de dos ventanas de 30 s distintas) para confirmar.</li>
          </ol>

          {qrDataUrl && (
            <div className={styles.qrWrapper}>
              <img src={qrDataUrl} alt="QR de autenticación" className={styles.qrImage} />
            </div>
          )}

          <div className={styles.secretBox}>
            <span className={styles.secretLabel}>Clave manual:</span>
            <code className={styles.secretCode}>{secret}</code>
          </div>

          <form onSubmit={handleActivate} className={styles.codeForm}>
            <div className={styles.codeGroup}>
              <label className={styles.codeLabel}>Primer código</label>
              <input
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={code1}
                onChange={e => setCode1(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                required
              />
            </div>
            <p className={styles.codeHint}>Espera al siguiente código (30 s) e ingrésalo abajo</p>
            <div className={styles.codeGroup}>
              <label className={styles.codeLabel}>Segundo código</label>
              <input
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={code2}
                onChange={e => setCode2(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                required
              />
            </div>

            {errorMsg && <p className={styles.error}>{errorMsg}</p>}

            <div className={styles.actions}>
              <button type="button" className={styles.btnSecondary} onClick={goBack}>Cancelar</button>
              <button type="submit" className={styles.btnPrimary} disabled={loading}>
                {loading ? 'Activando…' : 'Activar 2FA'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Vista: desactivar ──────────────────────────────────────────────────────
  if (view === 'disable') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Desactivar 2FA</h2>
          <p className={styles.statusDesc}>
            Ingresa el código actual de tu aplicación autenticadora para confirmar la desactivación.
          </p>

          <form onSubmit={handleDisable} className={styles.codeForm}>
            <div className={styles.codeGroup}>
              <label className={styles.codeLabel}>Código de verificación</label>
              <input
                className={styles.codeInput}
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code"
                required
              />
            </div>

            {errorMsg && <p className={styles.error}>{errorMsg}</p>}

            <div className={styles.actions}>
              <button type="button" className={styles.btnSecondary} onClick={goBack}>Cancelar</button>
              <button type="submit" className={styles.btnDanger} disabled={loading}>
                {loading ? 'Desactivando…' : 'Desactivar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ── Vista: éxito ───────────────────────────────────────────────────────────
  if (view === 'success') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.successIcon}>
            <CheckIcon />
          </div>
          <h2 className={styles.cardTitle}>{enabled ? '2FA Activado' : '2FA Desactivado'}</h2>
          <p className={styles.statusDesc}>{successMsg}</p>
          <button className={styles.btnPrimary} onClick={goBack}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return null;
}
